import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaRegStickyNote, FaShoppingCart, FaBox } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import FloatingSidebarButton from '../../components/FloatingSidebarButton/FloatingSidebarButton';
import CategoryIcons from '../../components/CategoryIcons/CategoryIcons';
import BusinessAccounts from '../../components/BusinessAccounts/BusinessAccounts';
import './Search.css';

const Search = () => {
  const { userProfile: authUserProfile } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchFilter, setSearchFilter] = useState("All"); // All, Users, Posts, Products
  const navigate = useNavigate();
  const { translations: tRaw } = useLanguage();
  const t = { ...tRaw, feed: { ...tRaw?.feed } };

  // Search functionality
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      const fetchSearchResults = async () => {
        setIsSearching(true);

        try {
          const results = [];

          // Fetch based on selected filter
          if (searchFilter === "All" || searchFilter === "Posts") {
            // Fetch posts from Supabase
            const { data: postsData, error: postsError } = await supabase
              .from('posts')
              .select('*')
              .eq('is_archived', false)
              .ilike('caption', `%${searchTerm}%`)
              .order('created_at', { ascending: false })
              .limit(50);

            if (postsError) {
              console.error("Error fetching posts:", postsError);
            } else if (postsData) {
              const matchedPosts = postsData.map(post => {
                // Check multiple possible field names for the user who posted
                const userId = post.posted_by_id || post.user_id || post.postedById;
                
                return {
                  id: post.id,
                  ...post,
                  postedById: userId,
                  posted_by_id: userId,
                  caption: post.caption
                };
              });
              
              results.push(...matchedPosts.map(p => ({ type: "post", ...p })));
            }
          }

          if (searchFilter === "All" || searchFilter === "Users") {
            // Fetch users/profiles from Supabase
            const { data: profilesData, error: profilesError } = await supabase
              .from('profiles')
              .select('user_id, username, display_name, avatar_url')
              .or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`)
              .limit(50);

            if (profilesError) {
              console.error("Error fetching profiles:", profilesError);
            } else if (profilesData) {
              const matchedUsers = profilesData.map(profile => {
                // Resolve avatar URL if it's a filename
                let profileImage = profile.avatar_url;
                if (profile.avatar_url && !profile.avatar_url.startsWith('http') && !profile.avatar_url.startsWith('data:')) {
                  try {
                    const path = `${profile.user_id}/${profile.avatar_url}`;
                    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
                    profileImage = data?.publicUrl || profile.avatar_url;
                  } catch (e) {
                    console.warn("Error resolving avatar URL:", e);
                  }
                }

                return {
                  id: profile.user_id,
                  username: profile.username || profile.display_name,
                  display_name: profile.display_name,
                  profileImage: profileImage,
                  type: "user"
                };
              });
              results.push(...matchedUsers);
            }
          }

          if (searchFilter === "All" || searchFilter === "Products") {
            // Fetch products from Supabase
            const { data: productsData, error: productsError } = await supabase
              .from('products')
              .select('*')
              .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
              .order('created_at', { ascending: false })
              .limit(50);

            if (productsError) {
              console.error("Error fetching products:", productsError);
              // Products table might not exist, ignore error
            } else if (productsData) {
              results.push(...productsData.map(p => ({ type: "product", ...p })));
            }
          }

          // Final client-side filter to ensure the displayed text contains the term
          const term = searchTerm.toLowerCase();
          const filtered = results.filter((item) => {
            if (!term) return false;
            switch (item.type) {
              case "user": {
                const userText = (item.username || item.display_name || "").toLowerCase();
                return userText.includes(term);
              }
              case "post": {
                const captionText = (item.caption || "").toLowerCase();
                return captionText.includes(term);
              }
              case "product": {
                const nameText = (item.name || "").toLowerCase();
                const descText = (item.description || "").toLowerCase();
                return nameText.includes(term) || descText.includes(term);
              }
              default:
                return false;
            }
          });

          setSearchResults(filtered);
        } catch (err) {
          console.error("Search failed:", err);
        }

        setIsSearching(false);
      };

      fetchSearchResults();
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, searchFilter]);

  function highlightMatch(text, term) {
    if (!term || !text) return text;
    const regex = new RegExp(`(${term})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  }

  const handleCategoryClick = async (catId) => {
    if (selectedCategory === catId) {
      setSelectedCategory(null);
      return;
    }
    setSelectedCategory(catId);
  };

  const getSearchResultIcon = (type) => {
    switch (type) {
      case "user":
        return <FaUser />;
      case "post":
        return <FaRegStickyNote />;
      case "product":
        return <FaBox />;
      default:
        return <FaRegStickyNote />;
    }
  };

  const getSearchResultText = (result) => {
    switch (result.type) {
      case "user":
        return result.username || result.display_name || "";
      case "post":
        return result.caption || "";
      case "product":
        return result.name || result.description?.slice(0, 40) + (result.description?.length > 40 ? "..." : "") || "";
      default:
        return "";
    }
  };

  const handleSearchResultClick = (result) => {
    switch (result.type) {
      case "user":
        navigate(`/profile/${result.id}`);
        break;
      case "post":
        // Use the correct field name from Supabase posts collection
        // Check multiple possible field names
        const postedById = result.postedById || result.posted_by_id || result.user_id;
        
        // Validate that we have a valid user ID
        if (postedById && typeof postedById === 'string' && postedById.trim() !== '' && postedById !== 'undefined') {
          navigate(`/profile_posts/${postedById}?tab=posts&scrollTo=${result.id}`);
        } else {
          console.error("Invalid or missing user ID for post. Post data:", {
            postId: result.id,
            postedById: result.postedById,
            posted_by_id: result.posted_by_id,
            user_id: result.user_id,
            fullPost: result
          });
          // Show an error to the user instead of navigating to an invalid route
          alert("Unable to navigate to post: Missing user information.");
        }
        break;
      case "product": {
        const vendorId = result.user_id || result.VendorId || result.vendor_id;
        if (vendorId && result.id) {
          navigate(`/product-details/${vendorId}/${result.id}`);
        }
        break;
      }
      default:
        console.warn("Unknown result type:", result.type);
        break;
    }
    setSearchTerm("");
    setSearchResults([]);
    setIsSearchFocused(false);
  };

  const handleFilterClick = (filter) => {
    setSearchFilter(filter);
    // Keep the search input focused
    document.querySelector('.search-input')?.focus();
  };

  return (
    <div className="search-page">
      <FloatingSidebarButton userProfile={authUserProfile} />
      
      {/* Search Bar Section */}
      <div className="search-top-section">
        <div className="search-bar-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder={t.search?.placeholder || "Search"} 
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                // Delay hiding to allow clicking on results and filter buttons
                setTimeout(() => setIsSearchFocused(false), 300);
              }}
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => {
                  setSearchTerm("");
                  setSearchResults([]);
                }}
              >
                &times;
              </button>
            )}
          </div>
          <button className="cart-button" onClick={() => navigate('/cart')}>
            <FaShoppingCart />
          </button>
        </div>

        {/* Search Filter Row */}
        {isSearchFocused && (
  <div className="search-filter-row">
    {[
      { key: "All", label: t.search?.filters?.all || "All" },
      { key: "Users", label: t.search?.filters?.users || "Users" },
      { key: "Posts", label: t.search?.filters?.posts || "Posts" },
      { key: "Products", label: t.search?.filters?.products || "Products" }
    ].map((filter) => (
      <button
        key={filter.key}
        className={`search-filter-btn ${searchFilter === filter.key ? 'active' : ''}`}
        onClick={() => handleFilterClick(filter.key)}
        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
      >
        {filter.label}
      </button>
    ))}
  </div>
)}

        {/* Search Results Dropdown */}
        {searchTerm && isSearchFocused && (
          <div className="search-results-dropdown">
            {isSearching ? (
              <div className="search-result-item no-result">
                Searching...
              </div>
            ) : searchResults.length === 0 ? (
              <div className="search-result-item no-result">
                {t.feed?.noResults || "No results found"}
              </div>
            ) : (
              searchResults.map((result) => (
                <div
                  key={result.id}
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                >
                  {result.type === "user" ? (
                    result.profileImage ? (
                      <img
                        src={result.profileImage}
                        alt={result.username || result.display_name || "User"}
                        className="search-profile-image"
                      />
                    ) : (
                      <span className="search-icon">
                        {getSearchResultIcon(result.type)}
                      </span>
                    )
                  ) : (
                    <span className="search-icon">
                      {getSearchResultIcon(result.type)}
                    </span>
                  )}
                  <span
                    className="search-label"
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(
                        getSearchResultText(result),
                        searchTerm
                      ),
                    }}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Category Icons Section */}
      <div className="category-section">
        <CategoryIcons
          onCategoryClick={handleCategoryClick}
          selectedCategoryId={selectedCategory}
        />
      </div>

      {/* Search Ads Section */}
      {/* <SearchAds /> */}

      {/* Business Accounts Section */}
      {/* <BusinessAccounts /> */}
    </div>
  );
};

export default Search;
